import React from "react";

// ERP-5170. The breakdown under "Pricing Saved Successfully".
//
// Three counts, and the point of the third one is that **Pending is not Failed**:
// a Lazada campaign freezes a SKU's price and refuses every edit until it ends, so
// ERP keeps the new price and pushes it by itself once the campaign lifts
// (retry_lazada_locked_prices, daily 13:00 SGT). A push deferred past the save's
// time budget is the same story from the reader's side — "it will go out on its
// own" — so the backend counts both as pending and this only renders what it sent.
//
// The counts are computed server-side on purpose: the UI cannot tell a campaign
// lock from a deferred push without re-deriving the rule, and two copies would
// drift apart the first time either changed.

const dotStyle = (color) => ({
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: 99,
  background: color,
  flexShrink: 0,
});

const Count = ({ color, n, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
    <span style={dotStyle(color)} />
    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "Montserrat" }}>
      {n} {label}
    </span>
  </span>
);

// A save that only moved Base Prices pushes nothing to any platform, so all three
// counts come back 0. "0 Success - 0 Failed - 0 Pending" under a success title reads
// as though the save did nothing at all, which is the opposite of what happened. In
// that case the breakdown is dropped and the toast goes back to the plain
// "Pricing Saved Successfully" it always used to be. Callers ask this first, so the
// toast never enters its taller two-part layout with nothing to put in it.
export const hasPricingSaveDetails = (counts) => {
  const c = counts || {};
  return (
    Number(c.success || 0) + Number(c.failed || 0) + Number(c.pending || 0) > 0 ||
    Number(c.woo_base_updated || 0) > 0
  );
};

export default function PricingSaveSummary({ counts }) {
  const c = counts || {};
  const wooBase = Number(c.woo_base_updated || 0);
  const pushed = Number(c.success || 0) + Number(c.failed || 0) + Number(c.pending || 0);
  if (!hasPricingSaveDetails(counts)) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {pushed > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          <Count color="#146C43" n={Number(c.success || 0)} label="Success" />
          <Count color="#B02A37" n={Number(c.failed || 0)} label="Failed" />
          <Count color="#CC9A06" n={Number(c.pending || 0)} label="Pending" />
        </div>
      )}
      {wooBase > 0 && (
        // The rule above this line is in the reference design. Sampled off it as
        // #B3CDC1 on the toast's #D1E7DD, which is the toast's own text colour
        // (#0A3622) at 15% — so it is written that way rather than hard-coded, and
        // stays right if the toast's green ever shifts. It separates what happened to
        // the platform prices from what happened to the base prices: two different
        // things, and the second one is a consequence the user did not ask for.
        <div
          style={{
            fontSize: 11,
            fontWeight: 400,
            fontFamily: "Montserrat",
            // Only when a counts row sits above it - a Base-Price-only save shows
            // this line on its own and has nothing to be separated from.
            borderTop: pushed > 0 ? "1px solid rgba(10, 54, 34, 0.15)" : undefined,
            marginTop: pushed > 0 ? 6 : 0,
            paddingTop: pushed > 0 ? 6 : 0,
          }}
        >
          {wooBase} Base Price{wooBase > 1 ? "s" : ""} updated to match WooCommerce
        </div>
      )}
    </div>
  );
}
